/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { Table } from "@duxton-web/ui"
import { TableProps } from "@duxton-web/ui/es/table/"
import { FC, useState, useEffect } from "react"

export interface InfiniteTableProps extends TableProps<any> {
  initialLength?: number
  loadLength?: number
  loading?: boolean
}

export const InfiniteTable: FC<InfiniteTableProps> = ({
  dataSource,
  initialLength = 50,
  loadLength = 100,
  children,
  loading = false,
  ...rest
}: InfiniteTableProps) => {
  const [currentLength, setCurrentLength] = useState(initialLength)
  const [listenerIsSet, setListenerIsSet] = useState(false)

  const handleUserScrollDown = () => {
    const maxScroll = Math.max(
      document.body.scrollHeight,
      document.body.offsetHeight,
      document.documentElement.clientHeight,
      document.documentElement.scrollHeight,
      document.documentElement.offsetHeight,
    )
    const currentScroll: number = window.scrollY + window.innerHeight

    if (
      currentScroll >= maxScroll - 5 * window.innerHeight &&
      currentLength !== dataSource?.length
    ) {
      setCurrentLength(c =>
        c + loadLength > (dataSource as any).length
          ? (dataSource as any).length
          : c + loadLength,
      )
      // load more data
    }
  }
  useEffect(() => {
    if ((dataSource as any).length !== 0) {
      if (currentLength >= (dataSource as any).length) {
        window.removeEventListener("scroll", handleUserScrollDown)
        setListenerIsSet(false)
      }
    }
  }, [currentLength, dataSource])

  useEffect(() => {
    if (
      dataSource?.length !== 0 &&
      !listenerIsSet &&
      currentLength < (dataSource as any).length
    ) {
      window.addEventListener("scroll", handleUserScrollDown)
      setListenerIsSet(true)
    }
  }, [dataSource])

  return (
    <Table
      dataSource={dataSource}
      pagination={{
        total:
          (dataSource as any).length < initialLength
            ? dataSource?.length
            : currentLength,
        pageSize:
          (dataSource as any).length < initialLength
            ? dataSource?.length
            : currentLength,
        hideOnSinglePage: true,
      }}
      {...rest}
      loading={loading}
    >
      {children}
    </Table>
  )
}
